import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const BASE_ITEMS = [
  "Fast Delivery",
  "Free UK Delivery on Orders Over £40",
  "Premium Quality",
];

export const MarqueeBar = () => {
  const [saleText, setSaleText] = useState("");
  const [copies, setCopies] = useState(1);
  const measureRef = useRef(null);
  const viewportRef = useRef(null);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setSaleText((data.sale_banner_text || "").trim()))
      .catch(() => {});
  }, []);

  // Insert the live sale (admin-controlled) after "Free UK Delivery".
  const items = saleText
    ? [BASE_ITEMS[0], BASE_ITEMS[1], saleText, BASE_ITEMS[2]]
    : BASE_ITEMS;

  // Compute the minimum number of passes so that ONE group is at least as wide
  // as the viewport (never leaves a gap), while staying as small as possible.
  // A small animated layer avoids iOS Safari's GPU layer-width limit, which was
  // blanking out parts of the band on real devices.
  useLayoutEffect(() => {
    const recompute = () => {
      const pass = measureRef.current?.offsetWidth || 0;
      const vw = viewportRef.current?.offsetWidth || window.innerWidth || 0;
      if (pass > 0) setCopies(Math.max(1, Math.ceil((vw + 80) / pass)));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [saleText]);

  const Item = ({ text }) => (
    <span className="flex shrink-0 items-center">
      <span className="px-6 text-xs sm:text-sm font-medium tracking-[0.12em] uppercase whitespace-nowrap">{text}</span>
      <span className="text-black/40" aria-hidden="true">|</span>
    </span>
  );

  const Group = ({ aria }) => (
    <div className="flex shrink-0 items-center" aria-hidden={aria}>
      {Array.from({ length: copies }).map((_, c) =>
        items.map((text, i) => <Item key={`${c}-${i}`} text={text} />)
      )}
    </div>
  );

  return (
    <div ref={viewportRef} className="metallic-gold text-black overflow-hidden marquee-viewport" data-testid="marquee-bar">
      {/* Hidden single-pass measurer (not animated, off-flow). */}
      <div ref={measureRef} aria-hidden="true" className="flex items-center absolute -z-10 opacity-0 pointer-events-none" style={{ position: "absolute", left: "-9999px" }}>
        {items.map((text, i) => <Item key={i} text={text} />)}
      </div>
      <div className="flex w-max animate-marquee py-2">
        <Group />
        <Group aria={true} />
      </div>
    </div>
  );
};
